import { Controller } from 'stimulus';
import { mountTiptapEditor, destroyTiptapEditor } from './create_editor';
import EnrollmentStorage from '../storages/enrollment_storage';
import FormSimulate from '../components/form_simulate';
import AjaxLoader from '../utilities/ajax-loader';
import 'select2';
import 'select2/dist/css/select2';
import 'tippy.js/themes/light.css';
import './editor.scss';

// Shared base for the "document" editors (contracts and document templates). Both
// mount three editors (contract / header / footer) and share the same preview modal
// driven by a sample enrollment. Subclasses only provide the merge-tag groups for
// the main contract editor (and, for templates, swap them when the source changes).
//
// Kept out of app/packs/controllers so Stimulus' require.context never registers it
// as a standalone controller — it's an abstract base, not a mountable controller.
//
// Only the main contract editor gets merge tags: ContractToFile translates the
// `contract` field exclusively; header/footer are rendered raw.
export default class DocumentEditorController extends Controller {
  // Subclasses override to supply the current merge-tag groups for the contract.
  contractGroups() {
    return [];
  }

  mountDocumentEditors() {
    this.editors = [];

    const contractTextarea = this.element.querySelector('#contract-text');
    if (contractTextarea) {
      this.contractEditor = mountTiptapEditor(contractTextarea, {
        groups: this.contractGroups(),
        onPreview: () => this.openPreview()
      });
      this.editors.push(this.contractEditor);
    }

    ['#header-text', '#footer-text'].forEach((selector) => {
      const textarea = this.element.querySelector(selector);
      if (textarea) this.editors.push(mountTiptapEditor(textarea, {}));
    });

    // Bootstrap fires `show.bs.modal` through jQuery, so it must be bound with jQuery
    // — a native addEventListener never receives it.
    $('#modal-preview-contract').on('show.bs.modal', () => this.bindEnrollmentToPreview());
  }

  // Rebuilds the contract editor with a fresh merge-tag set, preserving its content.
  rebuildContractEditor(groups) {
    if (!this.contractEditor) return;
    const contractTextarea = this.element.querySelector('#contract-text');
    contractTextarea.value = this.contractEditor.getHTML();
    this.editors = this.editors.filter((editor) => editor !== this.contractEditor);
    destroyTiptapEditor(this.contractEditor);

    this.contractEditor = mountTiptapEditor(contractTextarea, {
      groups,
      onPreview: () => this.openPreview()
    });
    this.editors.push(this.contractEditor);
  }

  teardownEditors() {
    (this.editors || []).forEach((editor) => destroyTiptapEditor(editor));
    this.editors = [];
  }

  openPreview() {
    // Editors sync into their textareas on update; the preview reads those textareas.
    $('#modal-preview-contract').modal('show');
  }

  async bindEnrollmentToPreview() {
    const selectEnrollment = document.querySelector('#enrollment-preview.select2');
    if (!selectEnrollment || selectEnrollment.dataset.initialized) return;

    selectEnrollment.dataset.initialized = $(selectEnrollment).select2({
      data: await EnrollmentStorage.get(),
      placeholder: 'Encontrar Aluno',
      allowClear: true,
      dropdownParent: document.querySelector('#modal-preview-contract'),
      escapeMarkup: (markup) => markup,
      templateResult: this.toCapitalize,
      templateSelection: this.toCapitalize
    }).on('select2:select', (event) => {
      this.enrollmentId = event.params.data.id;
    });
  }

  toCapitalize(enrollment) {
    return `<span><span class="font-capitalize">${enrollment.text.toLowerCase()}</span></span>`;
  }

  preview() {
    const enrollmentId = $('#enrollment-preview.select2').val();
    if (!enrollmentId) return;

    const source = `${this.element.dataset.previewUrl}?enrollment_id=${enrollmentId}&d=${Date.now()}`;
    const inputs = [...this.element.querySelectorAll('input[name^="contract"], input[name^="document_template"], textarea')]
      .map((field) => field.outerHTML)
      .join('');
    const iframe = this.element.querySelector('iframe#iframe-preview-contract');

    AjaxLoader.show();
    FormSimulate.submit({
      method: 'post',
      action: source,
      inputs,
      appendTo: 'body',
      turbo: false,
      target: 'preview-contract'
    });
    iframe.addEventListener('load', () => AjaxLoader.hide());
  }
}
